import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress, 
  Alert, 
  IconButton,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Divider,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useAuthStore } from '../../store/authStore';

/**
 * Component for previewing a promotion as it would appear on the site
 */
const PromotionPreview = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  // State
  const [promotion, setPromotion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch promotion data
  useEffect(() => {
    const fetchPromotion = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/promotions/${id}`, {
          headers: {
            Authorization: `Bearer ${user.token}`
          }
        });
        
        setPromotion(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching promotion:', err);
        setError('Failed to load promotion data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    if (user && user.token) {
      fetchPromotion();
    }
  }, [id, user]);
  
  // Check if promotion is active based on dates and isActive flag
  const isActive = (promotion) => {
    if (!promotion.isActive) return false;
    
    const now = new Date();
    const startDate = promotion.startDate ? new Date(promotion.startDate) : null;
    const endDate = promotion.endDate ? new Date(promotion.endDate) : null;
    
    if (startDate && now < startDate) return false;
    if (endDate && now > endDate) return false;
    
    return true;
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Не указана';
    return format(new Date(dateString), 'dd MMMM yyyy', { locale: ru });
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/admin/promotions')}
          sx={{ mt: 2 }}
        >
          Вернуться к списку акций
        </Button>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/admin/promotions')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Предпросмотр акции
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/admin/promotions/edit/${id}`)}
        >
          Редактировать
        </Button>
      </Box>
      
      {/* Admin info panel */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Информация об акции
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Chip 
            label={isActive(promotion) ? 'Активна' : 'Неактивна'} 
            color={isActive(promotion) ? 'success' : 'default'}
          />
          <Chip 
            label={`Приоритет: ${promotion.priority || 0}`} 
            variant="outlined"
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Box>
            <Typography variant="body2" color="textSecondary">
              Дата начала:
            </Typography>
            <Typography variant="body1">
              {formatDate(promotion.startDate)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">
              Дата окончания:
            </Typography>
            <Typography variant="body1">
              {formatDate(promotion.endDate)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">
              ID акции:
            </Typography>
            <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
              {promotion.id}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">
              Создана:
            </Typography>
            <Typography variant="body1">
              {formatDate(promotion.createdAt)}
            </Typography>
          </Box>
        </Box>
      </Paper>
      
      {/* Preview card */}
      <Typography variant="h6" gutterBottom>
        Предпросмотр на сайте
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        backgroundColor: theme.palette.grey[100],
        p: 4,
        borderRadius: 1
      }}>
        <Card sx={{ 
          maxWidth: 800, 
          width: '100%',
          boxShadow: 3,
          overflow: 'hidden',
          borderRadius: 2
        }}>
          {promotion.imageUrl ? (
            <CardMedia
              component="img"
              height={isMobile ? 200 : 300}
              image={promotion.imageUrl}
              alt={promotion.title}
              sx={{ objectFit: 'cover' }}
            />
          ) : (
            <Box sx={{ 
              height: isMobile ? 200 : 300, 
              backgroundColor: theme.palette.grey[300],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography variant="body2" color="textSecondary">
                Нет изображения
              </Typography>
            </Box>
          )}
          <CardContent>
            <Typography variant="h5" component="div" gutterBottom>
              {promotion.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {promotion.description}
            </Typography>
            {(promotion.startDate || promotion.endDate) && (
              <Box sx={{ mt: 2 }}>
                {promotion.startDate && promotion.endDate ? (
                  <Typography variant="body2" color="text.secondary">
                    Акция действует с {formatDate(promotion.startDate)} по {formatDate(promotion.endDate)}
                  </Typography>
                ) : promotion.startDate ? (
                  <Typography variant="body2" color="text.secondary">
                    Акция действует с {formatDate(promotion.startDate)}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Акция действует до {formatDate(promotion.endDate)}
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
          {promotion.buttonText && promotion.buttonLink && (
            <CardActions>
              <Button size="medium" variant="contained" color="primary">
                {promotion.buttonText}
              </Button>
            </CardActions>
          )}
        </Card>
      </Box>
      
      {/* Mobile preview */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Мобильный вид
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        backgroundColor: theme.palette.grey[100],
        p: 4,
        borderRadius: 1
      }}>
        <Box sx={{ 
          maxWidth: 360, 
          width: '100%',
          border: '10px solid #000',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: 3
        }}>
          <Card sx={{ 
            width: '100%',
            boxShadow: 0,
            borderRadius: 0
          }}>
            {promotion.imageUrl ? (
              <CardMedia
                component="img"
                height={180}
                image={promotion.imageUrl}
                alt={promotion.title}
                sx={{ objectFit: 'cover' }}
              />
            ) : (
              <Box sx={{ 
                height: 180, 
                backgroundColor: theme.palette.grey[300],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography variant="body2" color="textSecondary">
                  Нет изображения
                </Typography>
              </Box>
            )}
            <CardContent>
              <Typography variant="h6" component="div" gutterBottom>
                {promotion.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {promotion.description}
              </Typography>
            </CardContent>
            {promotion.buttonText && promotion.buttonLink && (
              <CardActions>
                <Button size="small" variant="contained" color="primary">
                  {promotion.buttonText}
                </Button>
              </CardActions>
            )}
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default PromotionPreview;
